import { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import { Tooltip } from "react-tooltip";
import { Label, TextInput } from "flowbite-react";
import { FaInfoCircle } from "react-icons/fa";
import MultiSelectAsync from "./MultiSelectAsync";

const FormInput = ({ input, onChange, error }) => {
  const {
    id,
    name,
    placeholder,
    required,
    label,
    type,
    fetchOptions,
    mapResponse,
    options,
    tooltip,
    defaultValue,
    weights,
    defaultWeight,
  } = input;

  const color = error ? 'failure' : 'gray';

  const [intervalOperator, setIntervalOperator] = useState(null);
  const [intervalValue, setIntervalValue] = useState(null);
  const [intervalLabel, setIntervalLabel] = useState(null);
  const [currentWeights, setCurrentWeights] = useState([]);

  const handleChange = useCallback((value) => {
    onChange({ target: { name: id, value } });
  }, [id, onChange]);

  const handleMultiSelectChange = (value) => {
    const values = value.map((val) =>
      id === 'hasContactDetails'
        ? { contactType: val.value }
        : val.value
    );
    handleChange(values);
  }

  const handleAttrChange = (value, attribute) => {
    onChange({ target: { name: `${id}.${attribute}`, value } });
  }

  const handleWeightSelectChange = (value) => {
    const updatedWeights = currentWeights.filter((weight) =>
      value.some((item) => item.value === weight.id)
    );

    const newWeights = value
      .filter((item) =>
        !currentWeights.some((weight) => weight.id === item.value)
      )
      .map((item) => ({
        id: item.value,
        label: item.label,
        weight: defaultWeight,
      }));

    setCurrentWeights([...updatedWeights, ...newWeights]);
  }

  const handleWeightChange = (value, id) => {
    setCurrentWeights((prevWeights) =>
      prevWeights.map((weight) =>
        weight.id === id ? { ...weight, weight: value.value } : weight
      )
    );
  }

  useEffect(() => {
    const weightsWithoutLabel = currentWeights.map(({
      id,
      weight,
    }) => ({
      id,
      weight,
    }));

    onChange({
      target: {
        name: id,
        value: weightsWithoutLabel,
      }
    })
  }, [currentWeights, id, onChange]);

  useEffect(() => {
    const weightsWithoutLabel = currentWeights.map(({ id, weight }) => ({
      id,
      weight,
    }));

    handleChange(weightsWithoutLabel);
  }, [currentWeights, handleChange]);

  const renderSelectRange = () => (
    <div className="flex gap-2 text-sm w-full">
      <Select
        name={`${name}From`}
        onChange={(value) => handleAttrChange(value?.value, "min")}
        options={options[0]}
        placeholder="From"
        className="text-xs flex-1"
        isClearable
      />
      <Select
        name={`${name}To`}
        onChange={(value) => handleAttrChange(value?.value, "max")}
        options={options[1]}
        placeholder="To"
        className="text-xs flex-1"
        isClearable
      />
    </div>
  );

  const renderIntervalSelect = () => (
    <>
      <Select
        name={name}
        onChange={(value) => {
          handleAttrChange(value?.value, "interval");
          setIntervalLabel(value?.label);
          if (!intervalOperator) {
            handleAttrChange(defaultValue?.value?.operator, "operator");
          }
          if (!intervalValue) {
            handleAttrChange(defaultValue?.value?.value, "value");
          }
        }}
        options={options[0]}
        placeholder={placeholder}
        className="text-xs"
        isClearable
      />
      {intervalLabel && (
        <div className="flex justify-between gap-2 mt-2 items-center text-xs">
          {intervalLabel}
          <Select
            name={`${name}Value`}
            onChange={(value) => {
              handleAttrChange(value?.value?.operator, "operator");
              setIntervalOperator(value?.value?.operator);
              handleAttrChange(value?.value?.value, "value");
              setIntervalValue(value?.value?.value);
            }}
            options={options[1]}
            className="text-xs"
            defaultValue={defaultValue}
          />
        </div>
      )}
    </>
  );

  const renderMultiSelectWeight = (isAsync = false) => (
    <>
      {isAsync ? (
        <MultiSelectAsync
          placeholder={placeholder}
          required={required}
          fetchOptions={fetchOptions}
          mapResponse={mapResponse}
          onChange={handleWeightSelectChange}
        />
      ) : (
        <Select
          isMulti
          onChange={handleWeightSelectChange}
          placeholder={placeholder}
          className="text-xs"
          options={options}
          defaultValue={defaultValue}
        />
      )}
      {currentWeights.map((currentWeight, key) => (
        <div
          className="flex justify-between gap-2 mt-2 items-center text-xs"
          key={key}
        >
          {currentWeight.label}
          <Select
            name={`${name}Weight`}
            onChange={(value) =>
              handleWeightChange(value, currentWeight.id)
            }
            options={weights}
            className="text-xs"
            defaultValue={{
              label: `≥${defaultWeight * 100}%`,
              value: defaultWeight,
            }}
          />
        </div>
      ))}
    </>
  );

  const renderInput = () => {
    switch (type) {
      case 'multiSelectAsync':
        return (
          <MultiSelectAsync
            placeholder={placeholder}
            required={required}
            fetchOptions={fetchOptions}
            mapResponse={mapResponse}
            onChange={handleMultiSelectChange}
          />
        );
      case 'selectRange':
        return renderSelectRange();
      case 'select':
        return (
          <Select
            name={name}
            onChange={(value) => handleChange(value?.value)}
            options={options}
            placeholder={placeholder}
            className="text-xs"
            isClearable
          />
        );
      case 'multiSelect':
        return (
          <Select
            isMulti
            onChange={handleMultiSelectChange}
            placeholder={placeholder}
            className="text-xs"
            options={options}
            defaultValue={defaultValue}
          />
        );
      case 'intervalSelect':
        return renderIntervalSelect();
      case 'multiSelectWeightAsync':
        return renderMultiSelectWeight(true);
      case 'multiSelectWeight':
        return renderMultiSelectWeight(false);
      default:
        return (
          <TextInput
            id={id}
            type={type}
            name={name}
            placeholder={placeholder}
            required={required}
            onChange={onChange}
            color={color}
          />
        );
    }
  }

  return (
    <div>
      {label && (
        <div className="flex gap-2">
          <Label
            htmlFor={id}
            value={label}
            className="mb-2 block text-xs font-medium text-gray-900"
          />
          {tooltip && (
            <>
              <button data-tooltip-id="form-input-tooltip" data-tooltip-content={tooltip} className="mb-2">
                <FaInfoCircle />
              </button>
              <Tooltip
                id="form-input-tooltip"
                style={{
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
              />
            </>
          )}
        </div>
      )}
      <div className="flex">
        <div className="relative w-full">
          {renderInput()}
          {error?.message && (
            <p className="mt-2 text-sm text-red-600">{error.message}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default FormInput;
